import { useEffect, useRef, useState } from 'react'
import { messageFor } from '../lib/messages.ts'

/** How long the word "Copied" stays before the control reads Copy again. */
export const COPIED_RESET_MS = 3_000

/**
 * A Copy control for a value that is **not a secret** — a tenant id, a build key, a slug, a
 * nickname. docs/ux/PLATFORM-ADMIN.md §10.3 (the nickname) and §11.1 (the tenant id).
 *
 * - **It says Copied only after the clipboard write resolves.** The old system said "copied"
 *   whether or not it was (§10.3); here the word changes in the promise's continuation and
 *   nowhere else, so a refusal can never have been announced as a success first.
 * - **A refusal says so, in a sentence chosen by code** (§20.1), and the value stays on screen —
 *   the caller always renders the value beside this control, so selecting it by hand works.
 * - **The accessible name carries what is being copied** ("Copy tenant id"), because a page
 *   can hold three of these and "Copy, Copy, Copy" is not a list of controls anybody can use.
 *   It contains the visible word, so a voice-control user saying "click Copy" still lands.
 *   The outcome is announced through a status region, so the control's own name does not
 *   change under a screen reader's cursor.
 *
 * **Never use this for the activation link.** That value is a credential; it has its own
 * control with its own rules in `handoff.tsx` (masked by default, copy without revealing). This
 * component keeps no copy of what it was given beyond the render.
 */
export function CopyButton({
  value,
  noun,
  quiet = false,
}: {
  /** What goes on the clipboard. */
  readonly value: string
  /** What it is, for the accessible name and the announcement: "tenant id", "build key". */
  readonly noun: string
  /** The design's quiet (borderless) style, for a row that already has a lot on it. */
  readonly quiet?: boolean
}) {
  const [outcome, setOutcome] = useState<'idle' | 'copied' | 'refused'>('idle')
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(
    () => () => {
      clearTimeout(timer.current)
    },
    [],
  )

  const copy = async () => {
    clearTimeout(timer.current)
    try {
      await navigator.clipboard.writeText(value)
      setOutcome('copied')
      timer.current = setTimeout(() => {
        setOutcome('idle')
      }, COPIED_RESET_MS)
    } catch {
      // The thrown value is deliberately not inspected and never logged: it is the
      // browser's own permission text, and this application renders its own sentence.
      setOutcome('refused')
    }
  }

  return (
    <>
      <button
        type="button"
        className={quiet ? 'button button-quiet button-sm' : 'button button-sm'}
        aria-label={`Copy ${noun}`}
        onClick={() => {
          void copy()
        }}
      >
        {outcome === 'copied' ? 'Copied' : 'Copy'}
      </button>
      <span className="visually-hidden" role="status">
        {outcome === 'copied' ? `The ${noun} was copied to the clipboard.` : ''}
      </span>
      {outcome === 'refused' ? (
        <span className="copy-refused" role="alert">
          {messageFor('tenant', 'client_clipboard_denied')}
        </span>
      ) : null}
    </>
  )
}
